<template>
  <div class="inline-flex items-center gap-2">

    <span class="text-xs text-blue-600">{{ formattedDistance }}</span>
    <a
        :href="googleMapsUrl"
        class="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800"
        rel="noopener noreferrer"
        target="_blank"
    >
      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
        />
        <path
            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
        />
      </svg>
    </a>
    <a
        :href="directionsUrl"
        class="inline-flex items-center text-blue-600 hover:text-blue-800"
        rel="noopener noreferrer"
        target="_blank"
        title="Get directions"
    >
      <Navigation class="w-4 h-4"/>
    </a>
  </div>
</template>

<script setup>
import {computed} from "vue";
import {Navigation} from "lucide-vue-next";
import {useLocationStore} from "~/stores/useLocationStore.js";
import {calculateDistance} from "~/utils/distanceFunctions.js";
import {DEFAULT_LATITUDE, DEFAULT_LONGITUDE} from '~/composables/constants'
import {parseEWKBHex} from "~/utils/location_functions.js";

const props = defineProps({
  coordinates: {
    type: [String, Object],
    required: true,
  },
  schoolName: {
    type: String,
    required: true,
  },
});

const coordinates = computed(() => {
  try {
    return parseEWKBHex(props.coordinates);
  } catch (e) {
    console.error('Failed to parse coordinates:', e);
    return [0, 0];
  }
});

const formattedCoordinates = computed(() => {
  const [longitude, latitude] = coordinates.value;
  return `(${longitude.toFixed(6)}, ${latitude.toFixed(6)})`;
});

const {user} = useLocationStore();

const userCoordinates = computed(() => ({
  latitude: user?.location?.coordinates ? user?.location?.coordinates[1] : DEFAULT_LATITUDE,
  longitude: user?.location?.coordinates ? user?.location?.coordinates[0] : DEFAULT_LONGITUDE
}));


const distance = computed(() => {
  const [longitude, latitude] = coordinates.value;

  const {latitude: userLat, longitude: userLng} = userCoordinates.value;

  return calculateDistance(userLat, userLng, latitude, longitude);
});

const formattedDistance = computed(() => {

  return `${distance.value.toFixed(1)} km`;
});

const googleMapsUrl = computed(() => {

  const [longitude, latitude] = coordinates.value;
  const encodedName = encodeURIComponent(props.schoolName);

  return `https://www.google.com/maps/search/?api=1&query=${encodedName}&query=${latitude},${longitude}`;
});

const directionsUrl = computed(() => {

  const [longitude, latitude] = coordinates.value;
  const {latitude: userLat, longitude: userLng} = userCoordinates.value;

  return `https://www.google.com/maps/dir/?api=1&origin=${userLat},${userLng}&destination=${latitude},${longitude}`;
});
</script>